import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Box, Button, Grid, Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import PackageSelection from './PackageSelection';
import { getGeneralAPIRequest, fileDownload } from '../../utils/apiCommonMethods';
import { allowedIESOSite } from '../../apiConfig';

// ----------------------------------------------------------------------


const topOfCurrentHour = dayjs().startOf('hour');
const defaultStartDate = topOfCurrentHour.subtract(7, 'day');

// Helper function to get IESO default dates (yesterday and today)
const getIesoDefaultDates = () => {
  const yesterday = dayjs().subtract(1, 'day').startOf('day');
  const today = dayjs().startOf('day');
  return { startDate: yesterday, endDate: today };
};
// Helper function to generate custom filename based on site type
const generateFileName = (site, isIesoSite, currentTime) => {
  if (isIesoSite) {

      const currentDate = dayjs().format('YYYY-MM-DD');
      return `${site.name}_daily_reporting_${currentDate}.csv`;
  }
  // Default filename for non-IESO sites (with full timestamp)
  return `hourly_reporting_${currentTime}.csv`;
};

export async function getFileDownload(axiosSession, site, startDate, endDate, packages) {
  const paramsVar = new URLSearchParams();
  paramsVar.append("siteId", site.site_id);
  for (let i = 0; i < packages.length; i += 1) {
    paramsVar.append("packages", packages[i].apiName);
  }
  paramsVar.append("start", startDate.format());
  paramsVar.append("end", endDate.format());
  paramsVar.append("format", "csv");
  const apiRequest = {
    params: paramsVar,
    responseType: 'blob'
  };
  const apiResponse = await axiosSession.get('/reporting/detail', apiRequest);
  return apiResponse.data;
}

export function filterPackages(allPackages) {
  const detailPackages = [];
  for (let i = 0; i < allPackages.length; i += 1) {
    if (allPackages[i].detailReportingEnabled) {
      detailPackages.push(allPackages[i]);
    }
  }
  return detailPackages;
}

export function validateDates(selectedStartDate, selectedEndDate, siteReportingStartDate, isIesoFinancial) {
  if (selectedStartDate === null || selectedEndDate == null) {
    return 'Start Date and End Date Must be Populated';
  }
  if (selectedStartDate < siteReportingStartDate) {
    return `Start Date must be after ${siteReportingStartDate.format('MM/DD/YYYY')}`;
  }
  if (selectedEndDate > topOfCurrentHour) {
    return `End Date must in the past`;
  }
  if (selectedEndDate < selectedStartDate) {
    return `End Date must occur after Start Date`;
  }

  // Only validate hour precision for non-IESO financial packages

  if (isIesoFinancial) {
    // Enforce exactly one full day: start at 00:00, end = start + 1 day (00:00)
    const s = selectedStartDate.startOf('day');
    const e = selectedEndDate.startOf('day');
    if (!selectedStartDate.isSame(s)) return 'Start Date must be a full day (00:00)';
    if (!selectedEndDate.isSame(e)) return 'End Date must be a full day (00:00)';
    if (!e.isSame(s.add(1, 'day'))) return 'For IESO detail, select exactly one day (End = Start + 1 day)';
    return null;
  }
  if (selectedStartDate.minute() !== 0 || selectedEndDate.minute() !== 0) {
    return 'Dates must end at the top of an hour';
  }
  return null;
}

export default function ReportingDetailComponent({ msSessionInfo, singleSite }) {
  const [selectedStartDate, setStartDate] = React.useState(defaultStartDate);
  const [selectedEndDate, setEndDate] = React.useState(topOfCurrentHour);
  const [detailPackages, setPackages] = React.useState([]);
  const [progress, setProgress] = React.useState(100);
  const [errorMessage, setErrorMessage] = React.useState();
  const [selectedApiNames, setSelectedApiNames] = React.useState([]);
  const siteReportingStartDate = dayjs(singleSite.startDate);

  // Check if IESO Financial Detail package is present
  const isIesoFinancial = detailPackages.some(pkg => pkg.apiName === 'iesoFinancialDetail');

  // Check if this is an IESO site
  const isIesoSite = allowedIESOSite.has(singleSite.id);

  const handlePackageChange = (checkedValues) => {
    setSelectedApiNames(Array.isArray(checkedValues) ? checkedValues : []);
    if (checkedValues && checkedValues.length > 0) setErrorMessage(null);
  };
 const downloadData = async () => {
  if (!selectedApiNames || selectedApiNames.length === 0) {
    setErrorMessage('Please select at least one package.');
    return;
  }
  const dateErr = validateDates(selectedStartDate, selectedEndDate, siteReportingStartDate, isIesoFinancial);
  setErrorMessage(dateErr);
  if (dateErr !== null) return;
  const axiosSession = getGeneralAPIRequest(msSessionInfo);
  const interval = startLoadingTimer();

  try {
    const fileData = await getFileDownload(axiosSession, singleSite, selectedStartDate, selectedEndDate, detailPackages);
    const currentTime = dayjs().format();
    const fileName = generateFileName(singleSite, isIesoSite, currentTime);
    fileDownload(fileName, fileData);
    setErrorMessage(null);
  } catch (err) {
    if (err?.response?.status === 404) {
      setErrorMessage('File not found');
    } else {
      setErrorMessage('Failed to download file. Please try again.');
      console.error('Download error:', err);
    }
  } finally {
    endLoadingTimer(interval);
  }
};

  React.useEffect(() => {
    let calledOnceOnPageLoad = false;
    if (!calledOnceOnPageLoad && singleSite) {
      calledOnceOnPageLoad = true;
      const detailPackages = filterPackages(singleSite.availablePackages);
      setPackages(detailPackages);

      // Set default dates for IESO sites
      setSelectedApiNames([]);
      if (isIesoSite) {
        const iesoDates = getIesoDefaultDates();
        setStartDate(iesoDates.startDate);
        setEndDate(iesoDates.endDate);
      }
    }
  }, [singleSite, isIesoSite]);

  const startLoadingTimer = () => {
    setProgress(20);
    let counter = 20;
    return setInterval(() => {
      counter += 5;
      if (counter < 90) {
        setProgress(counter);
      }
    }, 1000);
  }

  const endLoadingTimer = (interval) => {
    clearInterval(interval);
    setProgress(100);
  }

  return (
    <>
      <Grid container spacing={3} direction="row" alignItems="flex-start" justifyContent="center">
        <Grid item xs={12} md={8} lg={8}>
          {errorMessage ? (
            <Typography variant="h6" sx={{ p: 0.5, pb: 5, textAlign: 'center' }}>
              {errorMessage}
            </Typography>
          ) : null}

          <Grid container spacing={3} direction="row" alignItems="stretch" justifyContent="center">
            <Grid item xs={12} md={12} lg={12}>
              {progress < 100 ? (
                <Box sx={{ width: '100%', p: 10 }}>
                  <Typography variant="body2" sx={{ p: 0.5, pb: 5, textAlign: 'center' }}>
                    Downloading Report...
                  </Typography>
                  <LinearProgress variant="determinate" value={progress} />
                </Box>
              ) : null}

              {progress === 100 &&
                (isIesoFinancial ? (
                  <DatePicker
                    label="Start Date"
                    sx={{ bgcolor: 'background.paper', width: 1 }}
                    minDate={siteReportingStartDate}
                    maxDate={dayjs().subtract(1, 'day').startOf('day')}
                    value={selectedStartDate}
                    onChange={(d) => {
                      if (!d) return;
                      const s = d.startOf('day');
                      setStartDate(s);
                      setEndDate(s.add(1, 'day'));
                    }}
                  />
                ) : (
                  <DateTimePicker
                    label="Start Date"
                    sx={{ bgcolor: 'background.paper', width: 1 }}
                    minDate={siteReportingStartDate}
                    maxDate={topOfCurrentHour}
                    value={selectedStartDate}
                    onChange={setStartDate}
                    views={['year', 'month', 'day', 'hours']}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                  />
                ))}
            </Grid>

            <Grid item xs={12} md={12} lg={12}>
              {progress === 100 &&
                (isIesoFinancial ? (
                  <DatePicker
                    label="End Date"
                    sx={{ bgcolor: 'background.paper', width: 1 }}
                    minDate={siteReportingStartDate.add(1, 'day')}
                    maxDate={topOfCurrentHour.startOf('day')}
                    value={selectedEndDate}
                    onChange={() => {}}
                    disabled
                    readOnly
                  />
                ) : (
                  <DateTimePicker
                    label="End Date"
                    sx={{ bgcolor: 'background.paper', width: 1 }}
                    minDate={siteReportingStartDate}
                    maxDate={topOfCurrentHour}
                    value={selectedEndDate}
                    onChange={setEndDate}
                    views={['year', 'month', 'day', 'hours']}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                  />
                ))}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={4} lg={4}>
          <Grid container spacing={3} direction="row" alignItems="stretch" justifyContent="center">
            <Grid item xs={12} md={12} lg={12}>
              <Button
                sx={{ width: 1 }}
                variant="contained"
                onClick={downloadData}
                disabled={selectedApiNames.length === 0}
              >
                <Typography variant="body2" sx={{ p: 0.5 }}>
                  Download Data
                </Typography>
              </Button>
            </Grid>
            <Grid item xs={12} md={12} lg={12}>
              <PackageSelection actionToTakeOnChange={handlePackageChange} allPackages={detailPackages} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

ReportingDetailComponent.propTypes = {
  msSessionInfo: PropTypes.object.isRequired,
  singleSite: PropTypes.object.isRequired,
};